import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { notificationService } from '../services/api';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    User,
    LogOut,
    Sun, 
    Moon, 
    Menu, 
    X,
    LayoutDashboard,
    FileText,
    ChevronDown,
    Bell
} from 'lucide-react';
import { format } from 'date-fns';

export const Header = () => {
    const { user, logout, isAuthenticated } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unread, setUnread] = useState(0);

    const refreshNotifications = useCallback(async () => {
        try {
            const [list, count] = await Promise.all([
                notificationService.list(),
                notificationService.unreadCount(),
            ]);
            setNotifications(list.data);
            setUnread(count.data.count);
        } catch {
            /* non-critical */
        }
    }, []);

    // Poll the notification feed while signed in.
    useEffect(() => {
        if (!isAuthenticated) return;
        refreshNotifications();
        const id = setInterval(refreshNotifications, 60000);
        return () => clearInterval(id);
    }, [isAuthenticated, refreshNotifications]);

    const openNotification = async (n) => {
        if (!n.read) {
            try { await notificationService.markRead(n.id); } catch { /* ignore */ }
            setUnread((u) => Math.max(0, u - 1));
            setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        }
        if (n.link) navigate(n.link);
    };

    const markAllRead = async () => {
        try { await notificationService.markAllRead(); } catch { /* ignore */ }
        setUnread(0);
        setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const getDashboardLink = () => {
        if (!user) return '/dashboard';
        if (user.role === 'admin') return '/admin';
        if (user.role === 'officer') return '/officer';
        return '/dashboard';
    };

    const navItems = [
        { label: 'Dashboard', href: getDashboardLink(), icon: LayoutDashboard },
    ];

    if (user?.role === 'user') {
        navItems.push({ label: 'My Reports', href: '/reports', icon: FileText });
    }

    return (
        <header className="sticky top-0 z-50 glass border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link 
                        to={isAuthenticated ? getDashboardLink() : '/'} 
                        className="flex items-center gap-2 group"
                        data-testid="logo-link"
                    >
                        <img
                            src="/logo.png"
                            alt="TrafficWatch logo"
                            className="h-9 w-auto rounded-md transition-transform group-hover:scale-105"
                        />
                        <span className="font-bold text-lg tracking-tight hidden sm:block">
                            TrafficWatch
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    {isAuthenticated && (
                        <nav className="hidden md:flex items-center gap-1">
                            {navItems.map((item) => (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                        location.pathname === item.href
                                            ? 'bg-primary/10 text-primary'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </nav>
                    )}

                    {/* Right Side */}
                    <div className="flex items-center gap-2">
                        {/* Theme Toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle"
                            className="h-9 w-9"
                        >
                            {theme === 'dark' ? (
                                <Sun className="h-4 w-4" />
                            ) : (
                                <Moon className="h-4 w-4" />
                            )}
                        </Button>

                        {isAuthenticated ? (
                            <>
                                {/* Notifications */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="relative h-9 w-9"
                                            data-testid="notifications-trigger"
                                            aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
                                        >
                                            <Bell className="h-4 w-4" />
                                            {unread > 0 && (
                                                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                                                    {unread > 9 ? '9+' : unread}
                                                </span>
                                            )}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-80">
                                        <div className="flex items-center justify-between px-2 py-2">
                                            <p className="text-sm font-medium">Notifications</p>
                                            {unread > 0 && (
                                                <button
                                                    onClick={markAllRead}
                                                    className="text-xs text-primary hover:underline"
                                                    data-testid="notifications-mark-all"
                                                >
                                                    Mark all read
                                                </button>
                                            )}
                                        </div>
                                        <DropdownMenuSeparator />
                                        <div className="max-h-80 overflow-y-auto">
                                            {notifications.length === 0 ? (
                                                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                                                    You're all caught up.
                                                </p>
                                            ) : (
                                                notifications.map((n) => (
                                                    <button
                                                        key={n.id}
                                                        onClick={() => openNotification(n)}
                                                        className={`w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-l-2 ${
                                                            n.read ? 'border-transparent' : 'border-primary bg-primary/5'
                                                        }`}
                                                        data-testid={`notification-${n.id}`}
                                                    >
                                                        <p className="text-sm font-medium leading-snug">{n.title}</p>
                                                        {n.body && (
                                                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                                                        )}
                                                        <p className="text-[10px] text-muted-foreground mt-1">
                                                            {format(new Date(n.created_at), 'MMM d, p')}
                                                        </p>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                {/* User Dropdown */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button 
                                            variant="ghost" 
                                            className="gap-2"
                                            data-testid="user-menu-trigger"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                {user?.picture ? (
                                                    <img 
                                                        src={user.picture} 
                                                        alt={user.name}
                                                        className="w-8 h-8 rounded-full"
                                                    />
                                                ) : (
                                                    <User className="w-4 h-4 text-primary" />
                                                )}
                                            </div>
                                            <span className="hidden sm:block text-sm font-medium">
                                                {user?.name?.split(' ')[0]}
                                            </span>
                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <div className="px-2 py-2">
                                            <p className="text-sm font-medium">{user?.name}</p>
                                            <p className="text-xs text-muted-foreground">{user?.email}</p>
                                            <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary capitalize">
                                                {user?.role}
                                            </span>
                                        </div>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={handleLogout}
                                            data-testid="logout-btn"
                                            className="text-destructive focus:text-destructive cursor-pointer"
                                        >
                                            <LogOut className="mr-2 h-4 w-4" />
                                            Logout
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                {/* Mobile Menu Button */}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="md:hidden"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    data-testid="mobile-menu-toggle"
                                >
                                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                                </Button>
                            </>
                        ) : (
                            <Link to="/login">
                                <Button data-testid="login-btn">Sign In</Button>
                            </Link>
                        )}
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isAuthenticated && mobileMenuOpen && (
                    <nav className="md:hidden py-4 border-t animate-fade-in">
                        <div className="flex flex-col gap-1">
                            {navItems.map((item) => (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                                        location.pathname === item.href
                                            ? 'bg-primary/10 text-primary'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    <item.icon className="h-4 w-4" />
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </nav>
                )}
            </div>
        </header>
    );
};
